import React, { Component } from "react";
import "./brand.css";
import MainLayout from "../../components/layouts/main";
import Constants from "../../data/constants";
import GridButton from "../../components/GridButton";
import Data from "../../data/data";
import GridAddButton from "../../components/GridAddButton";
import { motion } from "framer-motion";
import Animations from "../../animations/Animations";
import { Row, Col, Modal, List, message, Tooltip, Popconfirm } from "antd";
import { LayoutGrid, List as ListIcon, ChevronRight } from "lucide-react";
import CreateBrandPage from "../../components/popups/CreateBrandPage";
import PopupContainer from "../../components/popups/PopupContainer";
import PopupManager from "../../managers/PopupManager";
import APIManager from "../../managers/APIManager";
import TwoVertsSectionGridView from "../../components/views/TwoVertsSectionGridView";
import GridData from "../../data/GridData";
import CDXSearchBar from "../../components/searchbar";
import { connect } from "react-redux";
import {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
} from "../../actions";
import { constants } from "react-smooth-dnd";
import PermissionManager from "../../managers/PermissionManager";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

class BrandPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      brandsData: [],
      viewMode: localStorage.getItem("brand-view-mode") || "grid",
    };
  }

  componentDidMount() {
    console.log("this.props.usertoken " + this.props.usertoken);

    this.reloadPage();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.reloadBrandsSignal !== this.props.reloadBrandsSignal) {
      this.reloadPage();
    }
  }

  reloadPage = () => {
    this.props.requestServerGet(
      "get_brands",
      {},
      null,
      (response) => {
        const brands = response.data || [];
        this.setState({ brandsData: brands });

        // Auto-skip: if there's exactly 1 workspace, skip the selection page
        // and go straight to branches. This prevents confusion for Lite→Pro
        // users who see an auto-created workspace they never explicitly set up,
        // and is better UX in general (no point picking from a list of 1).
        // Users can always navigate back here via the logo or "Workspace Settings".
        if (brands.length === 1) {
          this.onBrandClicked(brands[0]);
        }
      },
      this.props.usertoken,
    );
  };

  launchCreateBrand = () => {
    PopupManager.launchCreateBrandPopup(this.reloadPage);
  };

  onBrandClicked = (brandData) => {
    const { selectBrand, selectPage } = this.props;
    selectBrand(brandData);
    selectPage(Constants.BRANCH_PAGE);
  };

  render() {
    const createPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_BRAND,
      PermissionManager.PERMISSION_TYPE_CREATE,
    );
    const editPermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_BRAND,
      PermissionManager.PERMISSION_TYPE_EDIT,
    );
    const deletePermission = PermissionManager.checkPermission(
      PermissionManager.PERMISSION_KEY_BRAND,
      PermissionManager.PERMISSION_TYPE_DELETE,
    );

    const { globalSearch, requestServerPost, usertoken } = this.props;
    const { viewMode, brandsData } = this.state;
    const t = (PRO_TRANSLATIONS[this.props.lang] || PRO_TRANSLATIONS.en).brand;

    const filteredBrands = this.state.brandsData
      ? this.state.brandsData
          .filter((data) =>
            data.BrandName.toLocaleLowerCase().includes(
              globalSearch.toLocaleLowerCase(),
            ),
          )
          .sort((a, b) => (a.BrandName < b.BrandName ? -1 : 1))
      : [];

    const handleDelete = (data) => {
      requestServerPost(
        "delete_brand",
        { brandid: data.BrandID },
        {},
        () => {
          message.success(t.deleteSuccess(data.BrandName));
          this.reloadPage();
        },
        usertoken,
      );
    };

    // ── Grid view
    const renderGrids = [
      createPermission ? (
        <motion.div
          key="add_btn"
          variants={Animations.LIST_ITEM}
          style={{ display: "inline-block" }}
        >
          <GridAddButton
            tooltip={t.createBrand}
            onClick={this.launchCreateBrand}
          />
        </motion.div>
      ) : null,
      ...filteredBrands.map((data) => {
        const buttonTitles = [];
        const buttonActionCallbacks = [];
        if (editPermission) {
          buttonTitles.push(t.edit);
          buttonActionCallbacks.push(() =>
            PopupManager.launchEditBrandPopup(data, this.reloadPage),
          );
        }
        if (deletePermission) {
          buttonTitles.push(t.delete);
          buttonActionCallbacks.push(() => handleDelete(data));
        }
        return (
          <motion.div
            key={data.BrandID}
            variants={Animations.LIST_ITEM}
            style={{ display: "inline-block" }}
          >
            <GridButton
              menuActionTitles={buttonTitles}
              menuActionCallbacks={buttonActionCallbacks}
              onClick={() => this.onBrandClicked(data)}
              title={data.BrandName}
              icon={data.BrandLogo}
            />
          </motion.div>
        );
      }),
    ];

    // ── List view
    const renderList = (
      <motion.div
        key={"list-" + (brandsData.length > 0 ? "loaded" : "empty")}
        variants={Animations.LIST_CONTAINER}
        initial="hidden"
        animate="show"
        style={{ display: "flex", flexDirection: "column", gap: "8px" }}
      >
        {createPermission && (
          <motion.button
            variants={Animations.LIST_ITEM}
            onClick={this.launchCreateBrand}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 20px",
              borderRadius: "12px",
              border: "1.5px dashed #c7d2fe",
              background: "rgba(99,102,241,0.04)",
              cursor: "pointer",
              color: "#6366f1",
              fontSize: "14px",
              fontWeight: 600,
              width: "100%",
              textAlign: "left",
            }}
          >
            <span style={{ fontSize: "22px", lineHeight: 1 }}>+</span>
            {t.createBrand}
          </motion.button>
        )}
        {filteredBrands.map((data) => {
          const imgSrc =
            data.BrandLogo ||
            "/cms/assets/images/placeholders/grid_icon_placeholder.png";
          return (
            <motion.div
              key={data.BrandID}
              variants={Animations.LIST_ITEM}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "14px 20px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.8)",
                border: "1px solid rgba(226,232,240,0.8)",
                cursor: "pointer",
                transition: "all 0.18s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(238,242,255,0.95)";
                e.currentTarget.style.borderColor = "rgba(199,210,254,0.9)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.8)";
                e.currentTarget.style.borderColor = "rgba(226,232,240,0.8)";
              }}
              onClick={() => this.onBrandClicked(data)}
            >
              <img
                src={imgSrc}
                alt={data.BrandName}
                style={{
                  width: "44px",
                  height: "44px",
                  objectFit: "contain",
                  borderRadius: "10px",
                  border: "1px solid #f1f5f9",
                  background: "#fff",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  flex: 1,
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#0f172a",
                }}
              >
                {data.BrandName}
              </div>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
                onClick={(e) => e.stopPropagation()}
              >
                {editPermission && (
                  <button
                    onClick={() =>
                      PopupManager.launchEditBrandPopup(data, this.reloadPage)
                    }
                    style={{
                      padding: "6px 14px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      color: "#475569",
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    {t.edit}
                  </button>
                )}
                {deletePermission && (
                  <Popconfirm
                    title={t.deleteConfirm(data.BrandName)}
                    onConfirm={() => handleDelete(data)}
                    okText={t.delete}
                    okButtonProps={{ danger: true }}
                    cancelText={t.cancel}
                  >
                    <button
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: "6px 14px",
                        borderRadius: "8px",
                        border: "1px solid #fecaca",
                        background: "#fff",
                        color: "#dc2626",
                        fontSize: "13px",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      {t.delete}
                    </button>
                  </Popconfirm>
                )}
              </div>
              <ChevronRight
                size={18}
                color="#94a3b8"
                style={{ flexShrink: 0 }}
              />
            </motion.div>
          );
        })}
      </motion.div>
    );

    return (
      <MainLayout
        {...this.props}
        id={Constants.BRAND_PAGE}
        showSideMenu={false}
        showTitleBar={false}
        showTopBarBrandLogo={false}
        ignorPadding={true}
      >
        {/* Outer: clips glow orbs, prevents body scroll */}
        <div
          style={{
            width: "100%",
            minHeight: "100vh",
            background:
              "linear-gradient(160deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Ambient glow — top right */}
          <div
            style={{
              position: "absolute",
              width: "700px",
              height: "700px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 65%)",
              top: "-260px",
              right: "-260px",
              pointerEvents: "none",
            }}
          />
          {/* Ambient glow — bottom left */}
          <div
            style={{
              position: "absolute",
              width: "500px",
              height: "500px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 65%)",
              bottom: "-160px",
              left: "-160px",
              pointerEvents: "none",
            }}
          />

          {/* Inner: single scroll container, no visible scrollbar */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              minHeight: "100vh",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <div
              className="brand-scroll"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ marginBottom: "28px" }}
              >
                <img
                  style={{ height: "44px", objectFit: "contain" }}
                  src="/cms/assets/svgs/cdx_logo.svg"
                  alt="CodexSignage"
                />
              </motion.div>

              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
                style={{ textAlign: "center", marginBottom: "44px" }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 14px",
                    borderRadius: "20px",
                    background: "rgba(245,158,11,0.09)",
                    border: "1px solid rgba(245,158,11,0.22)",
                    marginBottom: "16px",
                  }}
                >
                  <span
                    style={{
                      color: "#d97706",
                      fontSize: "12px",
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {t.proEdition}
                  </span>
                </div>
                <div
                  className="brand-header-title"
                  style={{
                    fontWeight: 800,
                    color: "#0f172a",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                    marginBottom: "10px",
                  }}
                >
                  {t.selectBrand}
                </div>
                <div
                  style={{
                    fontSize: "15px",
                    color: "#64748b",
                    lineHeight: 1.6,
                  }}
                >
                  {t.subtitle}
                </div>
              </motion.div>

              {/* Brand card */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
                className="brand-card"
                style={{
                  background: "rgba(255,255,255,0.72)",
                  border: "1px solid rgba(226,232,240,0.9)",
                  borderRadius: "24px",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",
                  boxShadow:
                    "0 4px 40px rgba(99,102,241,0.07), 0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                {/* View toggle toolbar */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      background: "#f1f5f9",
                      borderRadius: "10px",
                      padding: "3px",
                      gap: "2px",
                    }}
                  >
                    {[
                      { mode: "grid", Icon: LayoutGrid, label: t.grid },
                      { mode: "list", Icon: ListIcon, label: t.list },
                    ].map(({ mode, Icon, label }) => (
                      <button
                        key={mode}
                        onClick={() => {
                          this.setState({ viewMode: mode });
                          localStorage.setItem("brand-view-mode", mode);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 14px",
                          borderRadius: "7px",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "13px",
                          fontWeight: 500,
                          transition: "all 0.15s",
                          background:
                            viewMode === mode ? "#ffffff" : "transparent",
                          color: viewMode === mode ? "#6366f1" : "#64748b",
                          boxShadow:
                            viewMode === mode
                              ? "0 1px 3px rgba(0,0,0,0.1)"
                              : "none",
                        }}
                      >
                        <Icon size={14} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content */}
                {viewMode === "grid" ? (
                  <motion.div
                    key={"grid-" + (brandsData.length > 0 ? "loaded" : "empty")}
                    variants={Animations.LIST_CONTAINER}
                    initial="hidden"
                    animate="show"
                    style={{
                      display: "inline-block",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    {renderGrids}
                  </motion.div>
                ) : (
                  renderList
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    globalSearch: state.globalSearch,
    selectedBrandData: state.selectedBrandData,
    selectedPage: state.selectedPage,
    reloadBrandsSignal: state.reloadBrandsSignal,
  };
};

const ConnectedBrandPage = connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
})(BrandPage);

const BrandPageWithLang = (props) => {
  const { lang } = useProLanguage();
  return <ConnectedBrandPage {...props} lang={lang} />;
};

export default BrandPageWithLang;
